import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import axios from "axios";
import { useFeedback } from "../contexts/FeedbackContext";
import { useQuota } from "../contexts/QuotaContext";
import {
  Search,
  Loader2,
  CheckCircle2,
  XCircle,
  Clock,
  ArrowRight,
  Trash2,
  Filter,
  Music2,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Download,
  FileJson,
  FileSpreadsheet,
  Archive,
  RotateCcw,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Debounce hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    
    return () => clearTimeout(handler);
  }, [value, delay]);
  
  return debouncedValue;
};

const statusConfig = {
  completed: { color: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20", icon: CheckCircle2 },
  failed: { color: "bg-red-500/10 text-red-400 border-red-500/20", icon: XCircle },
  searching: { color: "bg-amber-500/10 text-amber-400 border-amber-500/20", icon: Loader2 },
  queued: { color: "bg-blue-500/10 text-blue-400 border-blue-500/20", icon: Clock },
};

const Searches = () => {
  const [searches, setSearches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [viewMode, setViewMode] = useState("active"); // "active" or "archived"
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [deleting, setDeleting] = useState(null);
  const [restoring, setRestoring] = useState(null);
  const [exporting, setExporting] = useState(null);
  const { triggerFeedbackCheck } = useFeedback();
  const { unlimited, canUse } = useQuota();
  const canExport = unlimited || canUse("catalogue_import"); // Export requires paid tier
  const limit = 20;
  
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  const fetchSearches = useCallback(async () => {
    setLoading(true);
    try {
      if (viewMode === "archived") {
        const params = new URLSearchParams({
          limit: limit.toString(),
          skip: (page * limit).toString(),
        });
        const response = await axios.get(`${API}/searches/archived?${params}`);
        setSearches(response.data.searches || []);
        setTotal(response.data.total || 0);
      } else {
        const params = new URLSearchParams({
          limit: limit.toString(),
          skip: (page * limit).toString(),
        });
        if (statusFilter !== "all") params.append("status", statusFilter);
        if (sourceFilter !== "all") params.append("source", sourceFilter);
        if (debouncedSearchQuery.trim()) params.append("q", debouncedSearchQuery.trim());
        
        const response = await axios.get(`${API}/searches?${params}`);
        setSearches(response.data.searches || []);
        setTotal(response.data.total || 0);
      }
    } catch (error) {
      console.error("Failed to fetch searches:", error);
      toast.error("Failed to load searches");
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, sourceFilter, debouncedSearchQuery, viewMode]);

  useEffect(() => {
    fetchSearches();
  }, [fetchSearches]);

  const handleDelete = async (id, e) => {
    e.preventDefault();
    e.stopPropagation();
    setDeleting(id);
    try {
      await axios.delete(`${API}/search/${id}`);
      toast.success("Search archived — data retained in database");
      fetchSearches();
    } catch (error) {
      toast.error("Failed to archive search");
    } finally {
      setDeleting(null);
    }
  };

  const handleRestore = async (id, e) => {
    e.preventDefault();
    e.stopPropagation();
    setRestoring(id);
    try {
      await axios.patch(`${API}/search/${id}/restore`);
      toast.success("Search restored");
      fetchSearches();
    } catch (error) {
      toast.error("Failed to restore search");
    } finally {
      setRestoring(null);
    }
  };

  const handleExport = async (format) => {
    setExporting(format);
    try {
      const params = new URLSearchParams();
      
      if (statusFilter !== "all") {
        params.append("status", statusFilter);
      }
      if (sourceFilter !== "all") {
        params.append("source", sourceFilter);
      }
      if (debouncedSearchQuery.trim()) {
        params.append("q", debouncedSearchQuery.trim());
      }
      
      const response = await axios.get(`${API}/export/searches.${format}?${params}`, {
        responseType: 'blob'
      });
      
      // Extract filename from Content-Disposition header or generate one
      const contentDisposition = response.headers['content-disposition'];
      let filename = `searches_export.${format}`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) filename = match[1];
      }
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Exported ${total} searches as ${format.toUpperCase()}`);
      
      // Trigger feedback check after export
      triggerFeedbackCheck("export");
    } catch (error) {
      console.error("Export failed:", error);
      if (error.response?.status === 404) {
        toast.error("No searches found matching current filters");
      } else {
        toast.error(`Failed to export as ${format.toUpperCase()}`);
      }
    } finally {
      setExporting(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-6" data-testid="searches-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-syncchain-text">
            {viewMode === "archived" ? "Archived Searches" : "All Searches"}
          </h1>
          <p className="text-syncchain-text-secondary mt-1">
            {viewMode === "archived"
              ? "Archived searches — data retained for future reference"
              : "Browse and search through all completed track lookups"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Active / Archived Toggle */}
          <div className="flex bg-syncchain-surface rounded-lg p-1 border border-white/10">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setViewMode("active"); setPage(0); }}
              className={`text-xs px-3 ${viewMode === "active" ? "bg-syncchain-accent text-white" : "text-syncchain-text-muted hover:text-syncchain-text"}`}
              data-testid="view-active-btn"
            >
              <Music2 className="w-3 h-3 mr-1.5" />
              Active
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setViewMode("archived"); setPage(0); }}
              className={`text-xs px-3 ${viewMode === "archived" ? "bg-syncchain-accent text-white" : "text-syncchain-text-muted hover:text-syncchain-text"}`}
              data-testid="view-archived-btn"
            >
              <Archive className="w-3 h-3 mr-1.5" />
              Archived
            </Button>
          </div>
          {/* Export Buttons */}
          <Button
            onClick={() => canExport ? handleExport('csv') : toast.info("Export is available on Pro+ and above")}
            variant="outline"
            className={`border-white/10 text-syncchain-text-secondary hover:bg-white/5 ${!canExport ? 'opacity-50' : ''}`}
            disabled={exporting !== null || total === 0}
            data-testid="export-csv-btn"
          >
            {exporting === 'csv' ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <FileSpreadsheet className="w-4 h-4 mr-2" />
            )}
            CSV
          </Button>
          <Button
            onClick={() => canExport ? handleExport('json') : toast.info("Export is available on Pro+ and above")}
            variant="outline"
            className={`border-white/10 text-syncchain-text-secondary hover:bg-white/5 ${!canExport ? 'opacity-50' : ''}`}
            disabled={exporting !== null || total === 0}
            data-testid="export-json-btn"
          >
            {exporting === 'json' ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <FileJson className="w-4 h-4 mr-2" />
            )}
            JSON
          </Button>
          <Button
            onClick={fetchSearches}
            variant="outline"
            className="border-white/10 text-syncchain-text-secondary hover:bg-white/5"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Search and Filter Bar */}
      <Card className="bg-syncchain-paper border-white/10">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search Input */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-syncchain-text-muted" />
              <Input
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setPage(0);
                }}
                placeholder="Search by title, artist, or ISRC..."
                className="pl-10 bg-syncchain-surface border-white/10 text-syncchain-text"
                data-testid="search-input"
              />
            </div>
            
            {/* Status Filter */}
            <div className="w-full md:w-48">
              <Select value={statusFilter} onValueChange={(value) => { setStatusFilter(value); setPage(0); }}>
                <SelectTrigger className="bg-syncchain-surface border-white/10 text-syncchain-text">
                  <Filter className="w-4 h-4 mr-2 text-syncchain-text-muted" />
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent className="bg-syncchain-paper border-white/10">
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="searching">Searching</SelectItem>
                  <SelectItem value="queued">Queued</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Source Filter */}
            <div className="w-full md:w-48">
              <Select value={sourceFilter} onValueChange={(value) => { setSourceFilter(value); setPage(0); }}>
                <SelectTrigger className="bg-syncchain-surface border-white/10 text-syncchain-text">
                  <SelectValue placeholder="Filter by source" />
                </SelectTrigger>
                <SelectContent className="bg-syncchain-paper border-white/10">
                  <SelectItem value="all">All Sources</SelectItem>
                  <SelectItem value="single">Single Search</SelectItem>
                  <SelectItem value="catalogue">Catalogue Import</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          {/* Results count */}
          <div className="mt-3 text-sm text-syncchain-text-muted">
            {loading ? "Loading..." : `Showing ${searches.length} of ${total} searches`}
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-syncchain-accent" />
        </div>
      ) : searches.length === 0 ? (
        <Card className="bg-syncchain-paper border-white/10">
          <CardContent className="py-12 text-center">
            <Music2 className="w-12 h-12 text-syncchain-text-muted mx-auto mb-4" />
            <p className="text-syncchain-text-secondary">
              {searchQuery || statusFilter !== "all" 
                ? "No searches match your filters" 
                : "No searches yet"}
            </p>
            {!searchQuery && statusFilter === "all" && (
              <Button asChild className="mt-4 bg-syncchain-accent hover:bg-syncchain-accent-hover">
                <Link to="/search">Start a Single Song Search</Link>
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {searches.map((search) => {
            const StatusIcon = statusConfig[search.status]?.icon || Clock;
            const title = search.track_title || search.query_title || "Unknown";
            const artist = search.track_artist || search.query_artist || "Unknown";
            const isrc = search.results_json?.recording?.isrc;
            const label = search.results_json?.recording?.label;
            const imageUrl = search.results_json?.recording?.imageUrl;
            const album = search.results_json?.recording?.release;
            
            return (
              <Link
                key={search.id}
                to={`/search/${search.id}`}
                className="block"
                data-testid={`search-row-${search.id}`}
              >
                <Card className="bg-syncchain-paper border-white/10 hover:border-syncchain-accent/30 transition-all group">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4 flex-1 min-w-0">
                        {/* Album Artwork */}
                        <div className="w-12 h-12 rounded-lg bg-syncchain-surface flex items-center justify-center shrink-0 overflow-hidden">
                          {imageUrl ? (
                            <img 
                              src={imageUrl} 
                              alt={`${title} artwork`}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <Music2 className="w-6 h-6 text-syncchain-accent" />
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-medium text-syncchain-text truncate">{title}</h3>
                            <Badge variant="outline" className={statusConfig[search.status]?.color}>
                              <StatusIcon className={`w-3 h-3 mr-1 ${search.status === 'searching' ? 'animate-spin' : ''}`} />
                              {search.status}
                            </Badge>
                            {search.catalogue_job_id && (
                              <Badge variant="outline" className="bg-purple-500/10 text-purple-400 border-purple-500/20 text-xs">
                                Catalogue
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-syncchain-text-secondary truncate">{artist}</p>
                          <div className="flex items-center gap-3 mt-1 text-xs text-syncchain-text-muted">
                            {album && <span className="truncate max-w-[150px]">{album}</span>}
                            {isrc && <span className="font-mono">{album ? '•' : ''} {isrc}</span>}
                            {label && <span>• {label}</span>}
                            <span>• {formatDate(search.created_at)}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0 ml-4">
                        {viewMode === "archived" ? (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-syncchain-text-muted hover:text-syncchain-success hover:bg-syncchain-success/10"
                            onClick={(e) => handleRestore(search.id, e)}
                            disabled={restoring === search.id}
                            data-testid={`restore-search-${search.id}`}
                          >
                            {restoring === search.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <RotateCcw className="w-4 h-4" />
                            )}
                          </Button>
                        ) : (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-syncchain-text-muted hover:text-red-400 hover:bg-red-500/10"
                            onClick={(e) => handleDelete(search.id, e)}
                            disabled={deleting === search.id}
                          >
                            {deleting === search.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Trash2 className="w-4 h-4" />
                            )}
                          </Button>
                        )}
                        <ArrowRight className="w-5 h-5 text-syncchain-text-muted group-hover:text-syncchain-accent transition-colors" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-syncchain-text-muted">
            Page {page + 1} of {totalPages}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={page === 0}
              className="border-white/10 text-syncchain-text-secondary hover:bg-white/5"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              className="border-white/10 text-syncchain-text-secondary hover:bg-white/5"
            >
              Next
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Searches;
